import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const TEXT_MUTED = '#5a5f7d';

interface TimeBlock {
  hour: number;
  label: string;
  color: string;
}

export function TimeLedger() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const blockCount = 12;
    const blockW = 22;
    const blockH = 40;
    const gap = 3;
    const totalW = blockCount * (blockW + gap) - gap;
    const startX = (width - totalW) / 2;
    const topY = 30;

    const judgments: { label: string; color: string }[] = [
      { label: 'productive', color: '#4CAF50' },
      { label: 'wasted', color: '#E85D75' },
      { label: 'behind', color: '#FF9800' },
      { label: 'earned', color: '#2493A2' },
    ];

    const blocks: TimeBlock[] = d3.range(blockCount).map((i) => {
      const j = judgments[i % judgments.length];
      return { hour: 6 + i, label: j.label, color: j.color };
    });

    // Hour labels at top
    svg
      .selectAll('.hour-label')
      .data(blocks)
      .enter()
      .append('text')
      .attr('x', (_, i) => startX + i * (blockW + gap) + blockW / 2)
      .attr('y', topY - 6)
      .attr('text-anchor', 'middle')
      .attr('font-size', 7)
      .attr('class', 'viz-text-muted')
      .text((d) => `${d.hour}:00`);

    // Draw neutral blocks
    const blockGroup = svg
      .selectAll('.block')
      .data(blocks)
      .enter()
      .append('g')
      .attr('class', 'block');

    blockGroup
      .append('rect')
      .attr('x', (_, i) => startX + i * (blockW + gap))
      .attr('y', topY)
      .attr('width', blockW)
      .attr('height', blockH)
      .attr('rx', 3)
      .attr('fill', '#8a8fa3')
      .attr('opacity', 0.35);

    // Animate blocks coloring into judgments
    blockGroup
      .select('rect')
      .transition()
      .duration(400)
      .delay((_, i) => 800 + i * 150)
      .attr('fill', (d) => (d as unknown as TimeBlock).color)
      .attr('opacity', 0.75);

    // Add judgment labels below each block
    blockGroup
      .append('text')
      .attr('x', (_, i) => startX + i * (blockW + gap) + blockW / 2)
      .attr('y', topY + blockH + 14)
      .attr('text-anchor', 'middle')
      .attr('font-size', 6)
      .attr('font-weight', 600)
      .attr('fill', (d) => d.color)
      .attr('opacity', 0)
      .text((d) => d.label)
      .attr('transform', (_, i) => {
        const cx = startX + i * (blockW + gap) + blockW / 2;
        const cy = topY + blockH + 14;
        return `rotate(-45, ${cx}, ${cy})`;
      })
      .transition()
      .duration(300)
      .delay((_, i) => 1000 + i * 150)
      .attr('opacity', 1);

    // Draw a bracket/line underneath showing "moral ledger"
    const ledgerTimer = d3.timeout(() => {
      const bracketY = topY + blockH + 38;

      svg
        .append('line')
        .attr('x1', startX)
        .attr('y1', bracketY)
        .attr('x2', startX)
        .attr('y2', bracketY)
        .attr('stroke', TEXT_MUTED)
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.6)
        .transition()
        .duration(600)
        .attr('x2', startX + totalW);

      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', bracketY + 16)
        .attr('text-anchor', 'middle')
        .attr('font-size', 9)
        .attr('font-style', 'italic')
        .attr('class', 'viz-text-muted')
        .attr('opacity', 0)
        .text('moral ledger')
        .transition()
        .duration(400)
        .delay(300)
        .attr('opacity', 0.8);
    }, 800 + blockCount * 150 + 400);

    // Animate a "scale" icon appearing
    const scaleTimer = d3.timeout(() => {
      const cx = width / 2;
      const cy = topY + blockH / 2;

      svg
        .append('text')
        .attr('x', cx)
        .attr('y', cy + 3)
        .attr('text-anchor', 'middle')
        .attr('font-size', 18)
        .attr('fill', TEXT_MUTED)
        .attr('opacity', 0)
        .text('\u2696') // balance scale unicode
        .transition()
        .duration(500)
        .attr('opacity', 0.15);
    }, 800 + blockCount * 150 + 1200);

    return () => {
      ledgerTimer.stop();
      scaleTimer.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-clock" /> Clock &mdash; Time Moralization
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 160" />
      <p className="viz-container__caption">
        Time becomes moral judgment.
      </p>
    </div>
  );
}
